import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { UnknownAction } from 'redux';

import { CloudActions, CostCenter } from '@portal/portal-core';
import { LayoutContext } from 'components/layout/layout-context';
import { Button } from 'components/styled-primitives';
import ActionToolbar from 'components/table/table-controls/action-toolbar';
import FilterSelectionToolbar from 'components/table/table-controls/filter-selection-toolbar';
import LicenseSearchControls from 'components/super-admin/license-search-controls';
import { useLicenseSearch } from 'components/super-admin/license-search-controls/use-license-search';

import { CloudAdminActions } from 'actions';
import { SuperAdminActions } from 'ducks/super-client';
import { MODAL_TYPES, TOOL_BUTTON_NAMES } from 'constants/super-admin';
import {
  selectActiveFilters,
  selectActiveLicenseRows,
  selectCurrentLicense,
  selectInstitutionalLicenseSyncState,
  selectLicensesWithAllocatedSeats,
} from 'selectors/super-admin';
// utils is an untyped .js module — imports resolve as `any`.
import {
  buildLicenseDeepLink,
  convertArrayOfObjectsToCSV,
  downloadCSV,
  formatSuperAdminListForExport,
  getSuperAdminExportFilename,
} from 'utils';
import InstitutionalLicenseTable from './institutional-license-table';

type LicenseTableRow = React.ComponentProps<typeof InstitutionalLicenseTable>['licenses'][number];

// FjSortTable's own client-side pagination stays active even with its Rows/Page chrome hidden
// (see hidePagination below), so this has to stay well above the largest row count one page of
// cost centers (SEARCH_PAGE_SIZE in use-license-search.ts) can produce. Otherwise it would truncate rows with no control
// left to page past them.
const UNLIMITED_TABLE_PAGE_SIZE = 1000;

const omitFormatting: string[] = [
  'seatLimit',
  'maxSharedComputers',
  'costCenterName',
  'allocatedSeats',
  'activeSharedSeats',
];

interface FilterActionButtons {
  adminButton: React.ReactElement;
  createLicenseButton: React.ReactElement;
  manageLicenseButton: React.ReactElement;
  exportButton: React.ReactElement;
  clearFilterButton: React.ReactElement;
  backToSearchButton: React.ReactElement;
}

const InstitutionalLicense = (): React.ReactElement => {
  // `selectLicensesWithAllocatedSeats` resolves as `unknown` at this selector
  // boundary; its rows originate from untyped `.js` state.
  const licenses = useSelector(
    selectLicensesWithAllocatedSeats as (state: unknown) => LicenseTableRow[],
  );
  const selectedRows = useSelector(selectActiveLicenseRows);
  const selectedLicense = useSelector(selectCurrentLicense);
  const activeFilters = useSelector(selectActiveFilters);
  const syncState = useSelector(selectInstitutionalLicenseSyncState);
  const [dismissSyncPrompt, setDismissSyncPrompt] = useState(false);

  const dispatch = useDispatch();
  // These action creators return named action interfaces without an index
  // signature, so cast to UnknownAction at the dispatch boundary.
  const showModal = (modalType?: string, feature?: string) =>
    dispatch(CloudAdminActions.showModal(modalType, feature) as unknown as UnknownAction);
  const clearFilters = () => dispatch(SuperAdminActions.clearFilters() as unknown as UnknownAction);
  // `limit` is required by the typed signature; `undefined` is runtime-identical
  // to the original no-arg call.
  const getInstitutionalLicenses = () =>
    dispatch(CloudActions.getInstitutionalLicensesVerbose(undefined));
  const getAllCostCenterInvites = () => dispatch(CloudActions.adminGetAllActiveCostCenterInvites());

  const dismissDataSync = (): void => {
    dispatch(SuperAdminActions.dismissLicenseSync() as unknown as UnknownAction);
    setDismissSyncPrompt(true);
  };
  const retryDataSync = (): void => {
    dispatch(CloudActions.getInstitutionalLicensesVerbose(undefined));
  };

  useEffect(() => {
    if (syncState.syncFailed) {
      setDismissSyncPrompt(false);
    }
  }, [syncState.syncFailed]);

  const { confirmDownload } = useContext(LayoutContext);

  const {
    mode,
    setMode,
    searchString,
    setSearchString,
    productNames,
    setProductNames,
    offset,
    setOffset,
    searchRows,
    searchTotal,
    searchLimit,
    searchLoading,
    searchError,
    searchHitCount,
  } = useLicenseSearch<LicenseTableRow>(
    TOOL_BUTTON_NAMES.INSTITUTION_LICENSE,
    CostCenter.COST_CENTER_TYPES.institutional,
    () => {
      getInstitutionalLicenses();
      getAllCostCenterInvites();
    },
  );

  const exportList = (): void =>
    confirmDownload({
      filename: getSuperAdminExportFilename('super-admin-institutional'),
      title: 'Export List?',
      ext: 'csv',
      accept: {
        text: 'Export',
        callback: (editedFilename: string) =>
          downloadCSV({
            filename: editedFilename,
            csvData: convertArrayOfObjectsToCSV({
              data: formatSuperAdminListForExport(
                licenses as unknown as Parameters<typeof formatSuperAdminListForExport>[0],
              ),
              doNotFormatTheseFields: omitFormatting,
              keys: [
                'primaryEmail',
                'firstname',
                'lastname',
                'ownerID',
                'region',
                'country',
                'costCenterID',
                'status',
                'activityState',
                'type',
                'product',
                'productAccessId',
                'allocatedSeats',
                'activeSharedSeats',
                'seatLimit',
                'maxSharedComputers',
                'costCenterName',
                'whitelistDomains',
                'startDate',
                'endDate',
                'billingDate',
                'whitelistIgnore',
                'admins',
                'authorizedDevicesOverride',
                'features',
              ],
            }),
          }),
      },
    });

  const filterActionButtons = (): FilterActionButtons => {
    const isManageLicenseDisabled = !Object.keys(selectedRows).length;
    // selectActivefilters resolves as `unknown` at this selector boundary.
    const someFiltersAreActive = !!Object.values(activeFilters as Record<string, unknown>).filter(
      Boolean,
    ).length;
    const licenseAdminLink = selectedLicense && buildLicenseDeepLink(selectedLicense);

    return {
      adminButton: (
        <Button key="admin" disabled={!selectedLicense} href={licenseAdminLink} target="_blank">
          Admin View
        </Button>
      ),
      createLicenseButton: (
        <Button key="create-license" onClick={() => showModal(MODAL_TYPES.CREATE_NEW_LICENSE)}>
          Create License
        </Button>
      ),
      manageLicenseButton: (
        <Button
          key="manage-license"
          onClick={() => showModal(MODAL_TYPES.MANAGE_LICENSE)}
          disabled={isManageLicenseDisabled}
        >
          Manage License
        </Button>
      ),
      exportButton: (
        <Button key="export" onClick={exportList}>
          Export List
        </Button>
      ),
      clearFilterButton: (
        <Button key="clear" disabled={!someFiltersAreActive} onClick={() => clearFilters()}>
          Clear Filter
        </Button>
      ),
      backToSearchButton: (
        <Button key="back-to-search" onClick={() => setMode('search')}>
          Back to search
        </Button>
      ),
    };
  };

  const renderActionToolbar = (): React.ReactElement => {
    const {
      adminButton,
      createLicenseButton,
      manageLicenseButton,
      exportButton,
      clearFilterButton,
      backToSearchButton,
    } = filterActionButtons();

    // 'null' represents a spacer in the button layout. ActionToolbar's `buttons`
    // prop is declared in terms of a plain `{ key; props }` shape; the rendered
    // Button elements carry both fields at runtime, so cast at this boundary.
    // backToSearchButton is included here (rather than rendered as a plain sibling
    // Button next to ActionToolbar) so it gets the same ButtonWrapper spacing and
    // vertical centering every other action button gets — a standalone sibling
    // Button sat outside ActionToolbar's flex row and rendered top-aligned instead.
    return (
      <ActionToolbar
        buttons={
          [
            createLicenseButton,
            manageLicenseButton,
            adminButton,
            null,
            clearFilterButton,
            null,
            exportButton,
            null,
            backToSearchButton,
          ] as unknown as React.ComponentProps<typeof ActionToolbar>['buttons']
        }
      />
    );
  };

  // Create/Manage/Admin View work the same way regardless of which view produced the rows —
  // row selection (selectedRows/selectedLicense) tracks by license/cost-center id, not by
  // data source. Export List and Clear Filter are deliberately excluded here: Export List still
  // only makes sense against the legacy view's fully-loaded dataset (see this file's earlier
  // CSV-export scoping), and Clear Filter targets the legacy per-column filter chips, which
  // don't exist in this view (its own search/product controls have their own clear mechanism).
  const renderSearchActionToolbar = (): React.ReactElement => {
    const { adminButton, createLicenseButton, manageLicenseButton } = filterActionButtons();

    const buttons: (React.ReactElement | null)[] = [
      createLicenseButton,
      manageLicenseButton,
      adminButton,
      null,
    ];

    return <ActionToolbar buttons={buttons as React.ComponentProps<typeof ActionToolbar>['buttons']} />;
  };

  const isSearchMode = mode === 'search';
  const visibleLicenses = isSearchMode ? searchRows : licenses;

  return (
    <InstitutionalLicenseTable
      licenses={visibleLicenses}
      filterControls={
        isSearchMode ? (
          <LicenseSearchControls
            searchString={searchString}
            onSearchStringChange={setSearchString}
            productNames={productNames}
            onProductNamesChange={setProductNames}
            total={searchTotal}
            limit={searchLimit}
            offset={offset}
            onOffsetChange={setOffset}
            loading={searchLoading}
            onViewLegacy={() => setMode('legacy')}
            pageLicenseCount={searchRows.length}
            pageCostCenterCount={searchHitCount}
            error={searchError}
          />
        ) : (
          <FilterSelectionToolbar numberOfResults={licenses.length} />
        )
      }
      controls={isSearchMode ? renderSearchActionToolbar() : renderActionToolbar()}
      hidePagination={isSearchMode}
      defaultPageSize={isSearchMode ? UNLIMITED_TABLE_PAGE_SIZE : undefined}
      syncFailed={syncState.syncFailed && !dismissSyncPrompt}
      currentRequest={syncState.currentRequest}
      requestsCount={syncState.requestsCount}
      nextRequest={syncState.nextRequest}
      dismissed={dismissSyncPrompt}
      onRetrySync={retryDataSync}
      onDismissSyncPrompt={dismissDataSync}
    />
  );
};

export default InstitutionalLicense;
